import React, { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import moment from 'moment';
import { motion, AnimatePresence } from 'framer-motion'; // For adaptive feedback
import { useKernel } from '../hooks/AIKernel'; // The Symbolic Engine
import { Copy, ExternalLink } from 'lucide-react';
import './UrlList.css';

const ListItem = ({ shortLinkIdentifier, originalLink, visits, lastVisited }) => {
  // --- AI Kernel Integration ---
  const { agentState, reportBehavior } = useKernel();
  const [copied, setCopied] = useState(false);

  // Formatting defaults
  const displayShort = shortLinkIdentifier || 'no-short';
  const displayOriginal = originalLink || 'no-original-link';
  const displayVisits = visits || 0;
  const displayTime = lastVisited ? moment(lastVisited).fromNow() : 'no-last-visits';
  const safeOriginalUrl = (() => {
    try {
      const parsed = new URL(displayOriginal);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
    } catch {
      return null;
    }
  })();

  const handleCopy = () => {
    setCopied(true);
    
    // 🧠 AI Sensor: Report high-value utility interaction
    // Copying a link is a strong indicator of "Successful Conversion"
    reportBehavior('productivity_utility', 2, 'interest');
    reportBehavior('link_copied', 1, 'behavior');

    // Reset feedback after 2 seconds
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <li className={`link-history-item ${agentState.theme}`}>
      <div className="link">
        <div className="link-shorten url-body">
          <CopyToClipboard text={displayShort} onCopy={handleCopy}>
            <motion.button
              id="short-link"
              className="link-copy-trigger"
              type="button"
              aria-label={`Copy shortened link ${displayShort}`}
              // AI Actuator: Disable hover scaling if the user is frustrated
              whileHover={!agentState.ui.simplifyAnimations ? { scale: 1.02 } : {}}
              whileTap={{ scale: 0.98 }}
            >
              <span>{displayShort}</span>
              <Copy size={13} aria-hidden="true" />
            </motion.button>
          </CopyToClipboard>
          
          <div className="copy-message normal-text" aria-live="polite">
            <AnimatePresence mode="wait">
              {copied ? (
                <motion.span 
                  key="copied"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="copy-message__success"
                >
                  Copied to clipboard!
                </motion.span>
              ) : (
                <span key="instructions">Copy link</span>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="link-original normal-text" title={displayOriginal}>
          <span>{displayOriginal}</span>
          {safeOriginalUrl && (
            <a href={safeOriginalUrl} target="_blank" rel="noreferrer" aria-label="Open destination in a new tab">
              <ExternalLink size={12} aria-hidden="true" />
            </a>
          )}
          {displayOriginal}
        </div>
      </div>

      <div className="visits table-headings">
        {displayVisits}
      </div>

      <div className="last-visited table-headings">
        {displayTime}
      </div>
    </li>
  );
};

export default ListItem;